import type { SkuType } from '@fieldvision/contracts';
import { useDebouncedValue } from '@fieldvision/web-core';
import { useMemo, useState } from 'react';

import { useOutletOptions, useSkuOptions, useUserOptions } from '../../lib/reference.js';
import type { ComboboxOption } from './combobox.logic.js';
import { SearchMultiSelect } from './SearchMultiSelect.js';

/**
 * The multi-select versions of `ReferenceCombobox.tsx`'s three pickers — for the
 * report filters, where an outlet, a petugas or a SKU became "one or more".
 *
 * Each owns what `SearchMultiSelect` deliberately does not: the search text, the
 * debounce and the query — disabled until the picker is opened, for the reason
 * `ReferenceCombobox.tsx` gives. The search text is cleared on close, since here
 * it is only ever a search and never the name of what was chosen.
 */
function usePickerState() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const search = useDebouncedValue(query.trim());
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery('');
  };
  return { open, onOpenChange, query, setQuery, search };
}

interface PickerProps {
  value: readonly ComboboxOption[];
  onChange: (value: ComboboxOption[]) => void;
  allLabel: string;
  disabled?: boolean;
}

export function OutletMultiPicker({
  regionId,
  ...props
}: PickerProps & {
  /** Narrowed by the SERVER — see `useOutletOptions`. */
  regionId?: string;
}) {
  const state = usePickerState();
  const outlets = useOutletOptions({ search: state.search, regionId, enabled: state.open });
  const options = useMemo<ComboboxOption[]>(
    () =>
      (outlets.data?.data ?? []).map((outlet) => ({
        id: outlet.id,
        label: outlet.name,
        hint: `${outlet.code} · ${outlet.regionName}`,
      })),
    [outlets.data],
  );

  return (
    <SearchMultiSelect
      {...props}
      query={state.query}
      onQueryChange={state.setQuery}
      open={state.open}
      onOpenChange={state.onOpenChange}
      options={options}
      total={outlets.data?.total ?? options.length}
      loading={outlets.isLoading}
      error={outlets.error}
      noun="Outlet"
      searchPlaceholder="Cari nama atau kode Outlet"
      emptyLabel={(term) =>
        term ? `Tidak ada Outlet yang cocok dengan "${term}".` : 'Belum ada Outlet di master data.'
      }
    />
  );
}

export function UserMultiPicker(props: PickerProps) {
  const state = usePickerState();
  const users = useUserOptions({ search: state.search, enabled: state.open });
  const options = useMemo<ComboboxOption[]>(
    () =>
      (users.data?.data ?? []).map((user) => ({
        id: user.id,
        label: user.name,
        hint: `${user.nik} · ${user.roleName}`,
      })),
    [users.data],
  );

  return (
    <SearchMultiSelect
      {...props}
      query={state.query}
      onQueryChange={state.setQuery}
      open={state.open}
      onOpenChange={state.onOpenChange}
      options={options}
      total={users.data?.total ?? options.length}
      loading={users.isLoading}
      error={users.error}
      noun="Petugas"
      searchPlaceholder="Cari nama atau NIK"
      emptyLabel={(term) =>
        term ? `Tidak ada petugas yang cocok dengan "${term}".` : 'Belum ada petugas terdaftar.'
      }
    />
  );
}

export function SkuMultiPicker({
  type,
  ...props
}: PickerProps & {
  /** Omitted searches the whole catalogue — see `SkuCombobox`. */
  type?: SkuType;
}) {
  const state = usePickerState();
  const skus = useSkuOptions({
    ...(type ? { type } : {}),
    search: state.search,
    enabled: state.open,
  });
  const options = useMemo<ComboboxOption[]>(
    () => (skus.data?.data ?? []).map((sku) => ({ id: sku.id, label: sku.name, hint: sku.code })),
    [skus.data],
  );

  return (
    <SearchMultiSelect
      {...props}
      query={state.query}
      onQueryChange={state.setQuery}
      open={state.open}
      onOpenChange={state.onOpenChange}
      options={options}
      total={skus.data?.total ?? options.length}
      loading={skus.isLoading}
      error={skus.error}
      noun="SKU"
      searchPlaceholder="Cari nama atau kode SKU"
      emptyLabel={(term) =>
        term ? `Tidak ada SKU yang cocok dengan "${term}".` : 'Belum ada SKU di master data.'
      }
    />
  );
}
